/**
 * Join step four: which of the event's activities the participant joins.
 *
 * The draft starts with every activity selected, because a share link usually
 * means "I am in for the whole thing"; deselecting is the exception the user has
 * to make explicitly. At least one has to stay selected: a registration that
 * attends nothing has no plan to be part of, and silently turning an empty
 * selection back into "everything" told the member the opposite of what they had
 * just chosen.
 *
 * Each chip carries the activity name only. Whether the organizer requires an
 * activity is a constraint on the plan, not on this choice, so showing it here
 * made members read "required" as "you must pick this one" while the toggle
 * never changed that constraint anyway.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { normalizeActivities, type ActivityKey } from '@shared/activities'

import { ErrorAlert } from '../../../components/layout/states'
import { WizardFooter } from '../../../components/layout/wizard'
import { Card, CardContent } from '../../../components/ui/card'
import { Chip, ChipGroup } from '../../../components/ui/chip'
import { activityLabelKey } from '../../../lib/format'
import { useSharedOutlet } from '../shared-layout'
import { joinStepNeighbours, joinStepPath, useJoinDraft } from './layout'

export function JoinActivitiesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { shareToken, event } = useSharedOutlet()
  const { draft, update } = useJoinDraft(shareToken)
  const { previous, next } = joinStepNeighbours('activities')

  const [error, setError] = useState<string | null>(null)

  // Once the user has touched the selection, the default must stop applying:
  // otherwise deselecting the last activity would immediately re-select all.
  const touched = useRef(false)

  useEffect(() => {
    if (touched.current) {
      return
    }
    if (draft.attendingActivities.length === 0 && event.activities.length > 0) {
      update({ attendingActivities: [...event.activities] })
    }
  }, [draft.attendingActivities.length, event.activities, update])

  function toggle(activity: ActivityKey): void {
    touched.current = true
    const selected = new Set(draft.attendingActivities)
    if (selected.has(activity)) {
      selected.delete(activity)
    } else {
      selected.add(activity)
    }
    update({ attendingActivities: normalizeActivities([...selected]) })
    setError(null)
  }

  function goNext(): void {
    if (draft.attendingActivities.length === 0) {
      setError(t('join.activitiesRequired'))
      return
    }
    setError(null)
    if (next) {
      navigate(joinStepPath(shareToken, next))
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-3 pt-4 sm:pt-5">
          <p className="text-sm">{t('join.activitiesHint')}</p>

          <ChipGroup label={t('join.stepActivities')}>
            {event.activities.map((activity) => (
              <Chip
                key={activity}
                selected={draft.attendingActivities.includes(activity)}
                onClick={() => toggle(activity)}
              >
                {t(activityLabelKey(activity))}
              </Chip>
            ))}
          </ChipGroup>

          {error ? (
            <ErrorAlert message={error} />
          ) : null}
        </CardContent>
      </Card>

      <WizardFooter
        backTo={previous ? joinStepPath(shareToken, previous) : undefined}
        backLabel={t('common.back')}
        onNext={goNext}
        nextLabel={t('common.next')}
      />
    </div>
  )
}
